import React, { useContext } from 'react';
import { CheckCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { Alert, Tag } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import UbiquitiHostDeviceDetailsContainer from '@totem/components/ubiquiti/hosts/details/devices/deviceDetails/UbiquitiHostDeviceDetailsContainer';
import UbiquitiHostDeviceDetailsPanel from '@totem/components/ubiquiti/hosts/details/devices/deviceDetails/UbiquitiHostDeviceDetailsPanel';
import UbiquitiHostDevicesContext from '@totem/components/ubiquiti/hosts/details/devices/ubiquitiHostDevicesContext';
import {
  UbiquitiApiDataItem,
  UbiquitiDevice,
} from '@totem/components/ubiquiti/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import IBTableExportButton from '@totem/components/ui_components/antDesignOverrides/IBTableExportButton';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

const UbiquitiHostDevicesTable = () => {
  const { loading, error, devices } = useContext(UbiquitiHostDevicesContext);

  // None of state/model/firmwareVersion have a documented exhaustive enum,
  // so filter options are built from whatever values are actually present
  // in the current data rather than a guessed static list.
  const getValueFilters = (values: string[]) =>
    Array.from(new Set(values))
      .map((value) => ({ text: value, value }))
      .sort((compA, compB) => sortStringAscending(compA.text, compB.text));

  const stateFilters = getValueFilters(
    devices.map((device) => device.data.state),
  );
  const modelFilters = getValueFilters(
    devices.map((device) => device.data.model),
  );
  const firmwareFilters = getValueFilters(
    devices.map((device) => device.data.firmwareVersion),
  );

  const columns: ColumnProps<UbiquitiApiDataItem<UbiquitiDevice>>[] = [
    {
      title: 'Name',
      dataIndex: ['data', 'name'],
      key: 'name',
      sorter: (compA, compB) =>
        sortStringAscending(compA.data.name, compB.data.name),
    },
    {
      title: 'Model',
      dataIndex: ['data', 'model'],
      key: 'model',
      sorter: (compA, compB) =>
        sortStringAscending(compA.data.model, compB.data.model),
      filters: modelFilters,
      onFilter: (value, record) => record.data.model === value,
    },
    {
      title: 'MAC Address',
      dataIndex: ['data', 'macAddress'],
      key: 'macAddress',
      sorter: (compA, compB) =>
        sortStringAscending(compA.data.macAddress, compB.data.macAddress),
    },
    {
      title: 'IP Address',
      dataIndex: ['data', 'ipAddress'],
      key: 'ipAddress',
      sorter: (compA, compB) =>
        sortStringAscending(compA.data.ipAddress, compB.data.ipAddress),
    },
    {
      title: 'Firmware',
      dataIndex: ['data', 'firmwareVersion'],
      key: 'firmwareVersion',
      sorter: (compA, compB) =>
        sortStringAscending(
          compA.data.firmwareVersion,
          compB.data.firmwareVersion,
        ),
      filters: firmwareFilters,
      onFilter: (value, record) => record.data.firmwareVersion === value,
    },
    {
      title: 'Supported',
      dataIndex: ['data', 'supported'],
      key: 'supported',
      sorter: (compA, compB) =>
        Number(compA.data.supported) - Number(compB.data.supported),
      filters: [
        { text: 'Supported', value: true },
        { text: 'Not Supported', value: false },
      ],
      onFilter: (value, record) => record.data.supported === value,
      render: (_, record: UbiquitiApiDataItem<UbiquitiDevice>) =>
        record.data.supported ? (
          <CheckCircleOutlined style={{ color: 'green' }} />
        ) : (
          <MinusCircleOutlined />
        ),
    },
    {
      title: 'State',
      dataIndex: ['data', 'state'],
      key: 'state',
      sorter: (compA, compB) =>
        sortStringAscending(compA.data.state, compB.data.state),
      filters: stateFilters,
      onFilter: (value, record) => record.data.state === value,
      render: (_, record: UbiquitiApiDataItem<UbiquitiDevice>) => {
        const stateColors: Record<string, string> = {
          ONLINE: 'green',
          OFFLINE: 'red',
        };

        return (
          <Tag color={stateColors[record.data.state]}>{record.data.state}</Tag>
        );
      },
    },
  ];

  const exportHeaders = [
    'Name',
    'Model',
    'MAC Address',
    'IP Address',
    'Firmware',
    'Supported',
    'State',
  ];
  const exportRows = devices.map((device) => [
    device.data.name,
    device.data.model,
    device.data.macAddress,
    device.data.ipAddress,
    device.data.firmwareVersion,
    device.data.supported ? 'Yes' : 'No',
    device.data.state,
  ]);

  if (error) {
    return (
      <Alert
        type="warning"
        showIcon
        message="UniFi devices unavailable"
        description="No UniFi device data could be found for this host."
      />
    );
  }

  return (
    <IBTable<UbiquitiApiDataItem<UbiquitiDevice>>
      showSorterTooltip
      columns={columns}
      dataSource={devices}
      loading={loading}
      pagination={{ defaultPageSize: 10, showSizeChanger: true }}
      rowKey={(record) => record.data.id}
      footerLeft={
        <IBTableExportButton
          fileName="unifi-devices"
          headers={exportHeaders}
          rows={exportRows}
        />
      }
      expandable={{
        expandedRowRender: (record) => (
          <UbiquitiHostDeviceDetailsContainer
            hostId={record.hostId}
            siteId={record.siteId}
            deviceId={record.data.id}
          >
            <UbiquitiHostDeviceDetailsPanel />
          </UbiquitiHostDeviceDetailsContainer>
        ),
      }}
    />
  );
};

export default UbiquitiHostDevicesTable;
